/* eslint-disable @typescript-eslint/no-non-null-assertion */

import settings from 'settings'
import { Account, isEnterprisePlan } from 'features/account/types'
import { User } from 'features/users/types'
import { hasEditionRole } from 'features/users/model/user'

declare global {
  interface Window {
    Vitally: {
      init(token: string, api: string): void
      account(params: ReturnType<NPSService['prepareAccountPayload']>): void
      user(params: ReturnType<NPSService['prepareUserPayload']>): void
      nps(mode: 'show' | 'survey' | 'test', params: ReturnType<NPSService['prepareNpsPayload']>): void
    }
  }
}

export class NPSService {
  user: Partial<User> | undefined
  account: Partial<Account>

  constructor({ user, account }: { user: Partial<User> | undefined; account: Partial<Account> }) {
    this.user = user
    this.account = account
  }

  static isCartoEmail(email: string) {
    const domain = email.split('@')[1].toLowerCase()
    return domain === 'carto.com' || domain === 'cartodb.com'
  }

  shouldPromptNPS() {
    if (!this.account.plan) return false

    const isAtLeastEditor = hasEditionRole(this.user)
    const belongsToOrgThatHasToReportNPS = !this.account.suppressNps // usually external accounts
    const isEnterprise = isEnterprisePlan(this.account.plan.name)
    const isCartoEmail = this.user?.email && NPSService.isCartoEmail(this.user.email)

    const nps = isAtLeastEditor && belongsToOrgThatHasToReportNPS && isEnterprise && !isCartoEmail
    return nps // for quick tests
  }

  promptNPS() {
    if (!window.Vitally) throw new Error('Vitally NPS integration is not ready')

    const VITALLY_TOKEN = settings.vitallyToken ?? ''
    window.Vitally.init(VITALLY_TOKEN, 'https://api.vitally-eu.io/analytics/v1')

    // as per today, account preparation is not required, user is enough (after NPS Vitally fix). Leaving it for a while, for observation
    // const account = this.prepareAccountPayload()
    // window.Vitally.account(account)

    const user = this.prepareUserPayload()
    window.Vitally.user(user)

    const nps = this.prepareNpsPayload()

    /* Mode 'survey'|'show'|'test'
      'survey': for Vitally to decide if it presents or not the survey (and store data in Vitally). For prod
      'show': to always display the survey (and store data in Vitally). For dev
      'test': to always display the survey (and avoid storing data in Vitally). For dev
    */
    const surveyDisplayMode = 'survey'
    window.Vitally.nps(surveyDisplayMode, nps)
  }

  prepareAccountPayload() {
    const account = {
      accountId: this.account.id,
      traits: {
        name: this.account.name
      }
    }
    return account
  }

  prepareUserPayload() {
    const user = {
      userId: this.user!.user_id
      // Vitally expects an organizationId/accountId, but we don't have it. This causes an 422 error in the API, but it's fine.).
    }
    return user
  }

  prepareNpsPayload() {
    const nps = {
      productName: 'CARTO',
      delay: 1000,
      npsQuestion: 'How likely are you to recommend CARTO?',
      primaryColor: '#036FE2',
      followUpTitle: 'Thank you for your feedback!',
      followUpSubtitle: ({ productName, score }) => {
        return score < 9
          ? `What can we do to improve ${productName}?`
          : `Would you like to share any additional feedback?`
      }
    }

    return nps
  }
}
